import React, { useState, useEffect, useRef } from 'react';
import { searchProfessions, TICINO_PROFESSIONS } from '../../utils/professions';

interface ProfessionAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onAdd: (profession: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export const ProfessionAutocomplete: React.FC<ProfessionAutocompleteProps> = ({
  value,
  onChange,
  onAdd,
  placeholder = "Cerca una professione...",
  className = "",
  disabled = false
}) => {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Gestisce la ricerca in tempo reale
  useEffect(() => {
    if (value.trim()) {
      const results = searchProfessions(value, 8);
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
      setSelectedIndex(-1);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [value]);

  // Gestisce la navigazione con tastiera
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleSelectSuggestion(suggestions[selectedIndex]);
        } else if (value.trim()) {
          handleAddCustom();
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        inputRef.current?.blur();
        break;
    }
  };

  const handleSelectSuggestion = (profession: string) => {
    onAdd(profession);
    onChange('');
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  const handleAddCustom = () => {
    if (value.trim() && !TICINO_PROFESSIONS.includes(value.trim())) {
      onAdd(value.trim());
      onChange('');
      setShowSuggestions(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleInputFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleInputBlur = () => {
    // Delay per permettere il click sui suggerimenti
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }, 200);
  };

  return (
    <div className={`relative ${className}`}>
      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          disabled={disabled}
          className="flex-1 input text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
        />
        <button
          type="button"
          onClick={handleAddCustom}
          disabled={disabled || !value.trim()}
          className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Aggiungi
        </button>
      </div>

      {/* Dropdown dei suggerimenti */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-60 overflow-y-auto"
        >
          {suggestions.map((profession, index) => (
            <div
              key={profession}
              className={`px-3 py-2 cursor-pointer text-sm transition-colors ${
                index === selectedIndex
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100'
              }`}
              onClick={() => handleSelectSuggestion(profession)}
            >
              {profession}
            </div>
          ))}
          
          {/* Opzione per aggiungere professione custom */}
          {value.trim() && !TICINO_PROFESSIONS.includes(value.trim()) && (
            <div
              className={`px-3 py-2 cursor-pointer text-sm border-t border-gray-200 dark:border-gray-700 transition-colors ${
                selectedIndex === suggestions.length
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : 'hover:bg-green-50 dark:hover:bg-green-900/20 text-green-700 dark:text-green-300'
              }`}
              onClick={handleAddCustom}
            >
              <span className="flex items-center gap-2">
                <span>➕</span>
                <span>Aggiungi "{value.trim()}"</span>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}; 